<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';

const store = useStore();
const currentChat = computed(() => store.getters.getSelectedChat);
const iframeRef = ref(null);
const collapsed = ref(false);
const lastOrder = ref(null);
const previousOrders = ref([]);
const orderStats = ref(null);
const loadingOrder = ref(false);
const showHistory = ref(false);

const PANEL_APP_URL = '/panel-app/index.html';
const PANEL_KEY = '69cc189637de1fe6455ab233504606758d2c9f3a06388e84';

// iframe src: ilk conversation yüklenince parametreli URL ile set edilir, sonra değişmez
// (postMessage sonraki conversation değişikliklerini handle eder, src değişmez = panel state korunur)
const iframeSrc = ref(PANEL_APP_URL);

const sender = computed(() => currentChat.value?.meta?.sender || {});
const assignee = computed(() => currentChat.value?.meta?.assignee || {});

const senderName = computed(() => sender.value.name || '');
const senderInitials = computed(() => {
  const n = senderName.value.trim();
  if (!n) return '?';
  const parts = n.split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : n.slice(0, 2).toUpperCase();
});

const agentName = computed(() => assignee.value.name || '—');

const contactPhone = computed(() => sender.value.phone_number || '');
const contactCity = computed(
  () => sender.value.additional_attributes?.city || ''
);
const contactAddress = computed(() => {
  const description = sender.value.additional_attributes?.description || '';
  return description.startsWith('Adres: ')
    ? description.slice('Adres: '.length)
    : description;
});

function formatPhone(phone) {
  const digits = phone.replace(/\D/g, '').replace(/^90/, '');
  if (digits.length !== 10) return phone;
  return `0${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
}

const platformLabel = computed(() => {
  const inboxId = currentChat.value?.inbox_id;
  return inboxId === 4 || inboxId === 2 ? 'Ticimax' : 'Shopify';
});

const paymentLabels = {
  cod_nakit: 'Kapıda Nakit',
  cod_kredi: 'Kapıda Kredi',
  bank: 'Havale',
  iyzico: 'İyzico',
};

function formatDate(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatAmount(val) {
  if (val == null) return '';
  return (
    Number(val).toLocaleString('tr-TR', { minimumFractionDigits: 0 }) + ' ₺'
  );
}

async function fetchLastOrder(contactId) {
  showHistory.value = false;
  if (!contactId) {
    lastOrder.value = null;
    previousOrders.value = [];
    orderStats.value = null;
    return;
  }
  loadingOrder.value = true;
  try {
    const r = await fetch(
      `/ticimax-api/customer-orders?contact_id=${contactId}`,
      {
        headers: { 'X-Panel-Key': PANEL_KEY },
      }
    );
    const data = await r.json();
    const orders = data.orders || [];
    lastOrder.value = orders[0] || null;
    previousOrders.value = orders.slice(1);
    orderStats.value = data.stats || null;
  } catch {
    lastOrder.value = null;
    previousOrders.value = [];
    orderStats.value = null;
  } finally {
    loadingOrder.value = false;
  }
}

function panelUrl() {
  const conv = currentChat.value;
  if (!conv?.id) return PANEL_APP_URL;
  const s = conv.meta?.sender || {};
  const a = conv.meta?.assignee || {};
  const phone = (s.phone_number || '')
    .replace(/\D/g, '')
    .replace(/^90/, '')
    .replace(/^0/, '');
  const params = new URLSearchParams({
    conv_id: conv.id,
    contact_id: s.id || '',
    contact_name: s.name || '',
    agent_id: a.id || '',
    agent_name: a.name || '',
    inbox_id: conv.inbox_id || '',
    phone,
    // Cache-bust: panel-app/index.html 1 yıl cache'leniyor; her dashboard
    // yüklemesinde taze panel çekilsin diye değişen bir parametre ekliyoruz
    // (yoksa panel-app'te yaptığımız değişiklikler tarayıcıda görünmüyor).
    v: String(Date.now()),
  });
  return PANEL_APP_URL + '?' + params.toString();
}

function sendConversation(conv) {
  if (!conv?.id || !iframeRef.value?.contentWindow) return;
  iframeRef.value.contentWindow.postMessage(
    { type: 'chatwoot:conversation-changed', data: conv },
    window.location.origin
  );
}

// Açık olan konuşmanın ID'sini takip ediyoruz; SADECE gerçekten başka bir
// konuşmaya geçilince paneli yeniden yüklüyoruz (aynı konuşmada yeni mesaj
// geldiğinde panel sıfırlanmasın diye — bu önemli, yoksa temsilci çalışırken
// her mesajda panel resetlenir).
let loadedConvId = null;
watch(
  currentChat,
  conv => {
    fetchLastOrder(conv?.meta?.sender?.id);
    // KONUŞMA DEĞİŞTİĞİNDE paneli o konuşmaya özel TAZE yüklüyoruz. Eskiden iframe
    // bir kez yüklenip sonraki konuşmalar postMessage ile bildiriliyordu; ama
    // panelin tuttuğu conv_id bazen ESKİ konuşmada kalıyor, sipariş onay mesajı
    // YANLIŞ müşteriye gidiyordu. Taze yükleme bunu kökten engeller, sepet de
    // müşteriler arası karışmaz.
    if (conv?.id && conv.id !== loadedConvId) {
      loadedConvId = conv.id;
      iframeSrc.value = panelUrl();
    }
  },
  { immediate: true }
);

function onLoad() {
  // iframe yeniden yüklenirse (sayfa yenileme vb.) güncel conversation'ı postMessage ile gönder
  sendConversation(currentChat.value);
}

function openFullscreen() {
  window.open(panelUrl(), '_blank');
}
</script>

<template>
  <!-- Geniş ekran: 4. sütun (1024px+) -->
  <div
    v-if="!collapsed"
    class="hidden lg:flex flex-col h-full overflow-hidden bg-n-surface-2 border-l border-n-weak flex-shrink-0 w-[360px] min-w-[360px]"
  >
    <!-- Başlık -->
    <div
      class="flex items-center gap-2 px-3 py-2.5 border-b border-n-weak flex-shrink-0"
    >
      <span class="size-4 text-n-slate-11 i-lucide-shopping-cart" />
      <h3 class="text-sm font-medium truncate text-n-slate-12 flex-1">
        {{ $t('ORDERS.PANEL.TITLE') }}
      </h3>
      <button
        class="size-5 text-n-slate-9 hover:text-n-slate-12 flex items-center justify-center"
        title="Yeni sekmede aç"
        @click="openFullscreen"
      >
        <span class="i-lucide-external-link size-3.5" />
      </button>
      <button
        class="size-5 text-n-slate-9 hover:text-n-slate-12 flex items-center justify-center"
        title="Paneli gizle"
        @click="collapsed = true"
      >
        <span class="i-lucide-panel-right-close size-3.5" />
      </button>
    </div>

    <!-- Müşteri Kartı -->
    <div
      v-if="currentChat.id && senderName"
      class="flex-shrink-0 border-b border-n-weak bg-n-surface-1 px-3 py-3"
    >
      <!-- Müşteri satırı -->
      <div class="flex items-center gap-2.5 mb-2.5">
        <div
          class="size-9 rounded-full bg-n-brand flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
        >
          {{ senderInitials }}
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-1.5 flex-wrap">
            <span class="text-sm font-semibold text-n-slate-12 truncate">{{
              senderName
            }}</span>
            <span
              class="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
              :class="
                platformLabel === 'Ticimax'
                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                  : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              "
            >
              {{ platformLabel }}
            </span>
          </div>
          <div class="text-xs text-n-slate-9 mt-0.5">
            Temsilci:
            <span class="font-medium text-n-slate-11">{{ agentName }}</span>
          </div>
        </div>
      </div>

      <!-- İletişim bilgileri -->
      <div
        v-if="contactPhone || contactCity || contactAddress"
        class="text-[11px] text-n-slate-10 space-y-1 mb-2.5 pl-11"
      >
        <a
          v-if="contactPhone"
          :href="`tel:${contactPhone}`"
          class="flex items-center gap-1.5 hover:text-n-brand"
        >
          <span class="i-lucide-phone size-3 flex-shrink-0" />
          {{ formatPhone(contactPhone) }}
        </a>
        <div
          v-if="contactCity || contactAddress"
          class="flex items-start gap-1.5"
        >
          <span class="i-lucide-map-pin size-3 mt-0.5 flex-shrink-0" />
          <span class="leading-snug">
            <template v-if="contactAddress">{{ contactAddress
              }}<template v-if="contactCity">, </template></template>{{ contactCity }}
          </span>
        </div>
      </div>

      <!-- Son sipariş -->
      <div v-if="loadingOrder" class="text-xs text-n-slate-9 py-1">
        Sipariş bilgisi yükleniyor…
      </div>
      <div
        v-else-if="lastOrder"
        class="bg-n-surface-2 rounded-lg px-2.5 py-2 border border-n-weak"
      >
        <div class="flex items-center justify-between mb-1">
          <span
            class="text-[10px] font-bold text-n-slate-9 uppercase tracking-wide"
            >Son Sipariş</span>
          <span
            class="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
            :class="
              lastOrder.deliveredAt
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-amber-50 text-amber-700'
            "
          >
            {{ lastOrder.deliveredAt ? 'Teslim Edildi' : 'Devamda' }}
          </span>
        </div>
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold text-n-slate-12">#{{ lastOrder.orderNo }}</span>
          <span class="text-xs font-bold text-emerald-700">{{
            formatAmount(lastOrder.total)
          }}</span>
        </div>
        <div class="flex items-center justify-between mt-0.5">
          <span class="text-[11px] text-n-slate-9">{{
            paymentLabels[lastOrder.paymentMethod] || lastOrder.paymentMethod
          }}</span>
          <span class="text-[11px] text-n-slate-9">{{
            formatDate(lastOrder.createdAt)
          }}</span>
        </div>
        <div
          v-if="lastOrder.cargoStatus"
          class="text-[11px] text-n-slate-9 mt-0.5 truncate"
        >
          Kargo: {{ lastOrder.cargoStatus }}
        </div>
        <!-- İstatistikler -->
        <div
          v-if="orderStats && orderStats.total > 1"
          class="mt-1.5 pt-1.5 border-t border-n-weak flex gap-3"
        >
          <div class="text-center">
            <div class="text-xs font-bold text-n-slate-12">
              {{ orderStats.total }}
            </div>
            <div class="text-[10px] text-n-slate-9">Sipariş</div>
          </div>
          <div class="text-center">
            <div class="text-xs font-bold text-n-slate-12">
              {{ orderStats.delivered }}
            </div>
            <div class="text-[10px] text-n-slate-9">Teslim</div>
          </div>
          <div class="text-center">
            <div
              class="text-xs font-bold"
              :class="
                orderStats.returnRate > 30 ? 'text-red-600' : 'text-emerald-700'
              "
            >
              %{{ orderStats.returnRate }}
            </div>
            <div class="text-[10px] text-n-slate-9">İade</div>
          </div>
        </div>

        <!-- Önceki siparişler -->
        <div
          v-if="previousOrders.length"
          class="mt-1.5 pt-1.5 border-t border-n-weak"
        >
          <button
            class="w-full flex items-center justify-between text-[11px] font-medium text-n-slate-10 hover:text-n-slate-12"
            @click="showHistory = !showHistory"
          >
            <span>Önceki Siparişler ({{ previousOrders.length }})</span>
            <span
              :class="
                showHistory ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
              "
              class="size-3.5"
            />
          </button>
          <div
            v-if="showHistory"
            class="mt-1.5 space-y-1.5 max-h-48 overflow-y-auto"
          >
            <div
              v-for="order in previousOrders"
              :key="`${order.platform}-${order.orderNo}`"
              class="bg-n-surface-1 rounded px-2 py-1.5 border border-n-weak"
            >
              <div class="flex items-center justify-between">
                <span class="text-[11px] font-semibold text-n-slate-12">#{{ order.orderNo }}</span>
                <span class="text-[11px] font-bold text-emerald-700">{{
                  formatAmount(order.total)
                }}</span>
              </div>
              <div class="flex items-center justify-between mt-0.5">
                <span class="text-[10px] text-n-slate-9">{{
                  formatDate(order.createdAt)
                }}</span>
                <span
                  class="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  :class="
                    order.deliveredAt
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-amber-50 text-amber-700'
                  "
                >
                  {{ order.deliveredAt ? 'Teslim Edildi' : 'Devamda' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-xs text-n-slate-9 italic py-1">
        Sistemde henüz sipariş yok
      </div>
    </div>

    <!-- Sipariş Alma Paneli (iframe) -->
    <iframe
      ref="iframeRef"
      :src="iframeSrc"
      :title="$t('ORDERS.PANEL.TITLE')"
      class="flex-1 w-full bg-white border-0"
      @load="onLoad"
    />
  </div>

  <!-- Gizlenince çıkan küçük aç butonu -->
  <button
    v-else
    class="hidden lg:flex flex-col items-center justify-center h-full w-8 border-l border-n-weak bg-n-surface-2 text-n-slate-9 hover:text-n-slate-12 hover:bg-n-surface-3 flex-shrink-0"
    title="Sipariş panelini aç"
    @click="collapsed = false"
  >
    <span class="i-lucide-panel-right-open size-4" />
  </button>
</template>
